import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Icon } from '@iconify/react';
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  Card,
  ScrollShadow,
  Spinner,
} from '@nextui-org/react';

import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { useDocuments } from '@/hooks/queries/documents';
import {
  useBulkCreateOrder,
  useBulkReorderDocuments,
  useOrderedDocuments,
  useRemoveFromOrder,
} from '@/hooks/queries/ordered-documents';
import { DocumentDataType, OrderedDocument } from '@/types/doc';
import { SortableDocumentCardNew } from './components/sortable-document-card-new';

const DocumentReorderPage = () => {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  const [orderedDocuments, setOrderedDocuments] = useState<OrderedDocument[]>(
    []
  );

  const { data: documentsData, isLoading: isLoadingDocuments } = useDocuments();
  const {
    data: orderedDocumentsData,
    isLoading: isLoadingOrdered,
    refetch: refetchOrderedDocuments,
  } = useOrderedDocuments();

  const bulkCreateOrderMutation = useBulkCreateOrder();
  const bulkReorderMutation = useBulkReorderDocuments();
  const removeFromOrderMutation = useRemoveFromOrder();
  React.useEffect(() => {
    if (orderedDocumentsData?.results) {
      setOrderedDocuments(orderedDocumentsData.results);
    }
  }, [orderedDocumentsData?.results]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const availableDocuments = useMemo(() => {
    if (!documentsData?.results) return [];
    const orderedDocumentIds = new Set(
      orderedDocuments.map((od) => od.document.id)
    );
    return documentsData.results.filter(
      (doc) => !orderedDocumentIds.has(doc.id)
    );
  }, [documentsData, orderedDocuments]);

  const [activeId, setActiveId] = useState<number | null>(null);

  const handleDragStart = (event: { active: { id: any } }) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: {
    active: { id: any };
    over: { id: any } | null;
  }) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    if (active.id !== over.id) {
      setOrderedDocuments((items) => {
        const oldIndex = items.findIndex(
          (item) => item.document.id === active.id
        );
        const newIndex = items.findIndex(
          (item) => item.document.id === over.id
        );

        return arrayMove(items, oldIndex, newIndex);
      });
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleSaveOrder = async (): Promise<void> => {
    if (orderedDocuments.length === 0) {
      toast.error('No documents to save order for.');
      return;
    }

    try {
      // First, add any new documents to the order
      const newDocuments = orderedDocuments
        .filter((od) => od.id < 0)
        .map((od) => ({
          document_id: od.document.id,
        }));

      if (newDocuments.length > 0) {
        await bulkCreateOrderMutation.mutateAsync(newDocuments);
      }

      // Then, reorder all documents
      const documentOrders = orderedDocuments.map((od, index) => ({
        document_id: od.document.id,
        position: index + 1,
      }));

      await bulkReorderMutation.mutateAsync(documentOrders);
      refetchOrderedDocuments();

      // navigate(`/${tenantSlug}/documents`);
    } catch (error) {
      console.error('Failed to save document order:', error);
    }
  };

  const handleAddDocument = (document: DocumentDataType) => {
    // Create a new ordered document entry with temporary id
    const newOrderedDocument: OrderedDocument = {
      id: -document.id, // Use negative ID to mark as new/temporary
      rank: `${(orderedDocuments.length + 1) * 1000}.000000000000000`,
      document: {
        id: document.id,
        filename: document.filename,
        batch_id: document.batch_id,
        created_at: document.created_at,
        updated_at: document.updated_at,
        partner_ref: null,
      },
      // org: {
      //   id: document.org.id,
      //   name: document.org.name,
      //   slug: '', // These will be set by the server
      //   parent_org: null,
      // },
      // org_id: document.org.id,
      created_by: document.created_by,
      created_at: new Date().toISOString(),
      updated_by: document.updated_by,
      updated_at: new Date().toISOString(),
    };
    setOrderedDocuments((prev) => [...prev, newOrderedDocument]);
  };

  const handleRemoveDocument = async (orderedDocumentId: number) => {
    if (orderedDocumentId < 0) {
      // For temporary/new documents, just remove from local state
      setOrderedDocuments((prev) =>
        prev.filter((od) => od.id !== orderedDocumentId)
      );
    } else {
      // For existing documents, call the API
      try {
        await removeFromOrderMutation.mutateAsync(orderedDocumentId);
        // The query will be invalidated and refetched automatically via the mutation's onSuccess
      } catch (error) {
        console.error('Failed to remove document from order:', error);
      }
    }
  };

  const getFileIcon = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'mdi:file-pdf-box';
      case 'doc':
      case 'docx':
        return 'mdi:file-word-box';
      case 'txt':
        return 'mdi:file-document';
      case 'xls':
      case 'xlsx':
        return 'mdi:file-excel-box';
      default:
        return 'mdi:file-document-outline';
    }
  };

  console.log(orderedDocuments);

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <div className="px-6 py-2 border-b border-divider bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Breadcrumbs
              size="sm"
              classNames={{
                list: 'gap-2 text-sm',
              }}
            >
              <BreadcrumbItem
                onPress={() => navigate(`/${tenantSlug}/documents`)}
              >
                <span className="text-sm">Documents</span>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <span className="text-sm">Reorder Documents</span>
              </BreadcrumbItem>
            </Breadcrumbs>
          </div>
          <div className="flex gap-2">
            <Button
              variant="bordered"
              size="sm"
              onPress={() => navigate(`/${tenantSlug}/documents`)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              size="sm"
              onPress={handleSaveOrder}
              isLoading={
                bulkReorderMutation.isPending ||
                bulkCreateOrderMutation.isPending
              }
              isDisabled={orderedDocuments.length === 0}
            >
              Save Order
            </Button>
          </div>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex-1 grid grid-cols-[400px,1fr] gap-6 min-h-0">
          {/* Left Side - Available Documents */}
          <div className="flex flex-col min-h-0 py-4 px-4 border-r">
            <div className="mb-4">
              <h2 className="text-lg font-semibold mb-2">
                Available Documents ({availableDocuments.length})
              </h2>
              <p className="text-sm text-default-500">
                Click on a document to add it to the order
              </p>
            </div>

            <div className="flex-1 min-h-0 relative">
              {isLoadingDocuments || isLoadingOrdered ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="lg" />
                </div>
              ) : (
                <ScrollShadow className="h-[calc(100vh-12rem)] w-full">
                  <div className="space-y-2 pr-2 w-full mb-8">
                    {availableDocuments.map((document) => (
                      <div
                        key={document.id}
                        className="w-full bg-default-100 rounded-medium p-3 flex items-center gap-3 cursor-pointer hover:bg-default-200"
                        onClick={() => handleAddDocument(document)}
                      >
                        <div className="flex items-center gap-2">
                          <Icon
                            icon="lucide:plus"
                            className="text-default-400"
                          />
                          <Icon
                            icon={getFileIcon(document.filename)}
                            className="text-default-600 w-4 h-4"
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-sm truncate">
                            {document.filename}
                          </h4>
                          <p className="text-xs text-default-500">
                            Batch ID: {document.batch_id}
                          </p>
                        </div>
                      </div>
                    ))}
                    {availableDocuments.length === 0 &&
                      documentsData?.results &&
                      documentsData.results.length > 0 && (
                        <div className="flex items-center justify-center py-12 text-center text-default-400">
                          <div>
                            <Icon
                              icon="lucide:check-circle-2"
                              className="mx-auto mb-2 h-12 w-12"
                            />
                            <p>All documents have been added to the order.</p>
                          </div>
                        </div>
                      )}
                    {documentsData?.results?.length === 0 && (
                      <div className="flex items-center justify-center py-12 text-center text-default-400">
                        <div>
                          <Icon
                            icon="lucide:file-x"
                            className="mx-auto mb-2 h-12 w-12"
                          />
                          <p>No documents available.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </ScrollShadow>
              )}
            </div>
          </div>

          {/* Right Side - Ordered Documents */}
          <div className="flex flex-col min-h-0 overflow-hidden">
            <div className="px-6 py-4">
              <h2 className="text-lg font-semibold mb-2">
                Documents visible to user ({orderedDocuments.length})
              </h2>
              <p className="text-sm text-default-500">
                Drag and drop to reorder documents
              </p>
            </div>

            <Card
              className="flex-1 min-h-[70vh] border-none rounded-none"
              shadow="none"
            >
              <SortableContext
                items={orderedDocuments.map((od) => od.document.id)}
                strategy={rectSortingStrategy}
              >
                <ScrollShadow className="h-[calc(100vh-16rem)]">
                  <div className="space-y-4 p-4">
                    {orderedDocuments.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {orderedDocuments.map((orderedDocument, index) => (
                          <SortableDocumentCardNew
                            key={orderedDocument.document.id}
                            orderedDocument={orderedDocument}
                            index={index}
                            onRemove={handleRemoveDocument}
                            getFileIcon={getFileIcon}
                            isRemoving={removeFromOrderMutation.isPending}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-[400px] text-center">
                        <Icon
                          icon="mdi:file-document-multiple-outline"
                          className="text-default-300 text-6xl mb-4"
                        />
                        <h3 className="text-lg font-medium text-default-600 mb-2">
                          No documents in order
                        </h3>
                        <p className="text-default-500 text-sm">
                          Click on documents from the left to add them to your
                          order
                        </p>
                      </div>
                    )}
                  </div>
                </ScrollShadow>
              </SortableContext>
            </Card>
          </div>
        </div>
        <DragOverlay>
          {activeId ? (
            <SortableDocumentCardNew
              orderedDocument={
                orderedDocuments.find((doc) => doc.document.id === activeId)!
              }
              index={orderedDocuments.findIndex(
                (doc) => doc.document.id === activeId
              )}
              onRemove={handleRemoveDocument}
              getFileIcon={getFileIcon}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default DocumentReorderPage;
